import React from 'react'
import { 
  Clock, 
  Users, 
  Eye, 
  Copy, 
  Edit, 
  ChevronRight, 
  ChevronLeft,
  History 
} from 'lucide-react'
import { Button } from '../ui'
import { useThemeTokens } from '../../themes'
import type { TimesheetHistoryItem } from '../../contracts/timesheet'

interface TimesheetHistoryProps {
  isCollapsed: boolean
  onToggle: () => void
  currentDate: string
  historyData: TimesheetHistoryItem[]
  onDateSelect: (date: string) => void
  onCopyWorkers: (fromDate: string) => void
  onViewTimesheet: (date: string) => void
  className?: string
  'data-testid'?: string
}

export const TimesheetHistory: React.FC<TimesheetHistoryProps> = ({
  isCollapsed,
  onToggle,
  currentDate,
  historyData,
  onDateSelect,
  onCopyWorkers,
  onViewTimesheet,
  className,
  'data-testid': testId
}) => {
  const theme = useThemeTokens()

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const today = new Date()
    const diffDays = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'TODAY'
    if (diffDays === 1) return 'YESTERDAY'
    if (diffDays <= 6) {
      return date.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    }).toUpperCase()
  }

  // Format time for display
  const formatTime = (dateTimeString: string): string => {
    const date = new Date(dateTimeString)
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Get status info for display
  const getStatusInfo = (item: TimesheetHistoryItem) => {
    switch (item.status) {
      case 'draft':
        return {
          emoji: '💾',
          text: 'Draft',
          color: theme.colors.warning,
          time: item.lastSavedAt ? formatTime(item.lastSavedAt) : null
        }
      case 'submitted':
        return {
          emoji: '✅',
          text: 'Submitted',
          color: theme.colors.success,
          time: item.submittedAt ? formatTime(item.submittedAt) : null
        }
      case 'approved':
        return {
          emoji: '✅',
          text: 'Approved',
          color: theme.colors.success,
          time: item.submittedAt ? formatTime(item.submittedAt) : null
        }
      default:
        return {
          emoji: '📅',
          text: 'Empty',
          color: theme.colors.textSecondary,
          time: null
        }
    }
  }

  // Container styles
  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    transition: 'width 0.3s ease',
    width: isCollapsed ? '48px' : '280px',
    minHeight: '600px'
  }

  // Header styles
  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background
  }

  // History list styles
  const historyListStyles: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: isCollapsed ? 0 : theme.spacing.sm
  }

  // History item styles
  const getHistoryItemStyles = (isSelected: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: isCollapsed ? theme.spacing.xs : theme.spacing.sm,
    margin: isCollapsed ? theme.spacing.xs : `0 0 ${theme.spacing.sm} 0`,
    backgroundColor: isSelected ? `${theme.colors.primary}15` : 'transparent',
    border: `1px solid ${isSelected ? theme.colors.primary : 'transparent'}`,
    borderRadius: theme.borderRadius.sm,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative'
  })

  const historyItemHoverStyles: React.CSSProperties = {
    backgroundColor: `${theme.colors.primary}08`,
    borderColor: `${theme.colors.primary}40`
  }

  // Actions styles
  const actionsStyles: React.CSSProperties = {
    display: 'flex',
    gap: theme.spacing.xs,
    marginTop: theme.spacing.xs
  }

  if (isCollapsed) {
    return (
      <div style={containerStyles} className={className} data-testid={testId}>
        {/* Collapsed Header */}
        <div style={headerStyles}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            style={{ padding: theme.spacing.xs }}
          >
            <ChevronRight size={16} />
          </Button>
        </div>

        {/* Collapsed History Items */}
        <div style={historyListStyles}>
          {historyData.slice(0, 8).map((item) => {
            const isSelected = item.date === currentDate
            const statusInfo = getStatusInfo(item)
            
            return (
              <div
                key={item.date}
                style={getHistoryItemStyles(isSelected)}
                onClick={() => onDateSelect(item.date)}
                title={`${formatDate(item.date)} - ${statusInfo.text} - ${item.totalWorkers} workers`}
              >
                <div style={{ 
                  textAlign: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: isSelected ? theme.typography.fontWeight.bold : 'normal'
                }}>
                  {statusInfo.emoji}
                </div>
                <div style={{
                  fontSize: '10px',
                  textAlign: 'center',
                  color: theme.colors.textSecondary,
                  lineHeight: 1
                }}>
                  {item.totalWorkers}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyles} className={className} data-testid={testId}>
      {/* Header */}
      <div style={headerStyles}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: theme.spacing.sm,
          fontSize: theme.typography.fontSize.md,
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.colors.text
        }}>
          <History size={20} color={theme.colors.primary} />
          <span>Recent Days</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          style={{ padding: theme.spacing.xs }}
        >
          <ChevronLeft size={16} />
        </Button>
      </div>

      {/* History List */}
      <div style={historyListStyles}>
        {historyData.length === 0 ? (
          <div style={{
            padding: theme.spacing.lg,
            textAlign: 'center',
            color: theme.colors.textSecondary,
            fontSize: theme.typography.fontSize.sm
          }}>
            No recent timesheets
          </div>
        ) : (
          historyData.map((item) => {
            const isSelected = item.date === currentDate
            const statusInfo = getStatusInfo(item)
            
            return (
              <div
                key={item.date}
                style={getHistoryItemStyles(isSelected)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    Object.assign(e.currentTarget.style, historyItemHoverStyles)
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.borderColor = 'transparent'
                  }
                }}
                data-testid="history-item"
              >
                {/* Date and Status */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between' 
                }}>
                  <div style={{
                    fontSize: theme.typography.fontSize.sm,
                    fontWeight: isSelected ? theme.typography.fontWeight.bold : theme.typography.fontWeight.medium,
                    color: isSelected ? theme.colors.primary : theme.colors.text
                  }}>
                    {statusInfo.emoji} {formatDate(item.date)}
                  </div>
                </div>

                {/* Status and Time */}
                <div style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: statusInfo.color,
                  fontWeight: theme.typography.fontWeight.medium
                }}>
                  {statusInfo.text} {statusInfo.time && `${statusInfo.time}`}
                </div>

                {/* Workers and Hours */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: theme.spacing.md,
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.textSecondary 
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                    <Users size={12} />
                    <span>{item.totalWorkers} worker{item.totalWorkers !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                    <Clock size={12} />
                    <span>{item.totalHours.toFixed(1)}h</span>
                  </div>
                </div>

                {/* Actions */}
                {!isSelected && (
                  <div style={actionsStyles}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        onViewTimesheet(item.date)
                      }}
                      style={{ 
                        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                        fontSize: '11px',
                        height: 'auto'
                      }}
                    >
                      <Eye size={12} />
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        onCopyWorkers(item.date)
                      }}
                      style={{ 
                        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                        fontSize: '11px',
                        height: 'auto'
                      }}
                    >
                      <Copy size={12} />
                      Copy
                    </Button>
                  </div>
                )}

                {/* Resume Draft Action */}
                {isSelected && item.status === 'draft' && (
                  <div style={actionsStyles}>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDateSelect(item.date)
                      }}
                      style={{ 
                        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                        fontSize: '11px',
                        height: 'auto'
                      }}
                    >
                      <Edit size={12} />
                      Resume
                    </Button>
                  </div>
                )}
              </div>
            )
          })
        )}

        {/* View All Button */}
        {historyData.length > 0 && (
          <div style={{ 
            padding: theme.spacing.md,
            borderTop: `1px solid ${theme.colors.border}`,
            textAlign: 'center'
          }}>
            <Button
              variant="ghost"
              size="sm"
              style={{ 
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.textSecondary
              }}
            >
              📚 View All
            </Button>
          </div>
        )}
      </div>
    </div>
  )
} 